import type { ChatStreamHandle } from '@/types/widget'
import { API_BASE } from './index'

/**
 * Events published by the astrobot-shopper stream broker over the WebSocket
 * upgrade path. Shape mirrors astrobotstream.Event in the api repo. The
 * shopper backend forwards `token`, `completed`, `failed`, and `cancelled`.
 * `activity` and `thinking` are ack-and-dropped server-side but are typed
 * here so a future backend change is a UI-only lift.
 */
export type ChatStreamEvent =
  | { type: 'token'; jobID: string; sequence: number; delta: string }
  | { type: 'activity' | 'thinking'; jobID: string; delta: string; sequence?: number }
  | { type: 'completed' | 'failed' | 'cancelled'; jobID: string; data?: unknown }

export interface ChatStreamConnection {
  close: () => void
  /** Send `{ type: "cancel" }` (queued if the socket is still connecting). */
  cancel: () => void
  /** True after `cancel()` was called on this connection. */
  didCancel: () => boolean
}

type OpenChatStreamArgs = {
  stream: ChatStreamHandle
  onEvent: (event: ChatStreamEvent) => void
  /** Called once when the socket closes for any reason after being opened. */
  onClose?: (info: { wasClean: boolean; code: number }) => void
  /** Called on socket-level errors (network drop, upgrade rejected, etc). */
  onError?: (error: Error) => void
}

const STREAM_FAILURE_FALLBACK = 'Streaming reply failed. Please try again.'

const trimmedError = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const errorFromUnknown = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    try {
      return errorFromUnknown(JSON.parse(value))
    } catch {
      return undefined
    }
  }

  if (!value || typeof value !== 'object') return undefined

  const record = value as Record<string, unknown>
  return trimmedError(record.error) ?? errorFromUnknown(record.job) ?? errorFromUnknown(record.data)
}

/** User-facing text from a terminal `failed` stream event (`data.job.error`). */
export function errorFromFailedStreamEvent(event: ChatStreamEvent): string {
  return errorFromUnknown(event) ?? STREAM_FAILURE_FALLBACK
}

const sendCancelFrame = (socket: WebSocket) => {
  socket.send(JSON.stringify({ type: 'cancel' }))
}

/**
 * `new URL(path, base)` requires an absolute base. Local / proxied deployments
 * configure API_BASE as a relative path (e.g. `/api/v1`), which the URL
 * constructor rejects. Resolve those against the page origin first.
 */
export function resolveChatStreamBase(apiBase: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  const trimmed = apiBase?.trim()
  if (!trimmed) return origin
  return new URL(trimmed, origin).toString()
}

/**
 * Build the ws(s):// URL for the stream broker. Resolves the broker path
 * against the configured API_BASE so it works in both dev (relative paths)
 * and embed (absolute cross-origin apiBase).
 */
export function buildChatStreamUrl(stream: ChatStreamHandle, apiBase: string): string {
  const url = new URL(stream.path, resolveChatStreamBase(apiBase))
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.searchParams.set('wsAuthTicket', stream.wsAuthTicket)
  return url.toString()
}

/**
 * Open a job-scoped WebSocket to the astrobot stream broker. The connection
 * is single-use: the broker closes the socket after publishing a terminal
 * `completed` / `failed` / `cancelled` event. Callers own reconnect policy —
 * this module does not retry on its own.
 */
export function openChatStream({
  stream,
  onEvent,
  onClose,
  onError,
}: OpenChatStreamArgs): ChatStreamConnection {
  const url = buildChatStreamUrl(stream, API_BASE)
  const socket = new WebSocket(url)
  let closed = false
  let cancelRequested = false
  let pendingCancel = false

  const trySendCancel = () => {
    if (closed || socket.readyState !== WebSocket.OPEN) return
    sendCancelFrame(socket)
  }

  socket.onopen = () => {
    if (closed || !pendingCancel) return
    pendingCancel = false
    trySendCancel()
  }

  socket.onmessage = (event) => {
    if (closed) return
    try {
      const parsed = JSON.parse(String(event.data)) as ChatStreamEvent
      if (parsed && typeof parsed === 'object' && typeof parsed.type === 'string') {
        onEvent(parsed)
      }
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Failed to parse stream event'))
    }
  }

  socket.onerror = () => {
    if (closed) return
    onError?.(new Error('Chat stream socket error'))
  }

  socket.onclose = (event) => {
    if (closed) return
    closed = true
    onClose?.({ wasClean: event.wasClean, code: event.code })
  }

  return {
    close(): void {
      if (closed) return
      closed = true
      try {
        if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) {
          socket.close(1000, 'client closing')
        }
      } catch {
        // ignore; socket may already be tearing down
      }
    },
    cancel(): void {
      if (closed) return
      cancelRequested = true
      if (socket.readyState === WebSocket.OPEN) {
        trySendCancel()
        return
      }
      if (socket.readyState === WebSocket.CONNECTING) {
        pendingCancel = true
      }
    },
    didCancel(): boolean {
      return cancelRequested
    },
  }
}
